import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  Receipt, Plus, Search, DollarSign, Fuel, Wrench, Shield,
  FileText, Check, X, RefreshCw, Loader2, Calendar, Users, UserCheck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EXPENSE_CATEGORIES = [
  { value: 'fuel', label: 'Fuel', icon: Fuel },
  { value: 'tolls', label: 'Tolls', icon: Receipt },
  { value: 'repairs', label: 'Repairs', icon: Wrench },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench },
  { value: 'insurance', label: 'Insurance', icon: Shield },
  { value: 'licensing', label: 'Licensing', icon: FileText },
  { value: 'inspection', label: 'Inspection', icon: Check },
  { value: 'border_fees', label: 'Border Fees', icon: FileText },
  { value: 'driver_allowance', label: 'Driver Allowance', icon: DollarSign },
  { value: 'salaries', label: 'Salaries', icon: Users },
  { value: 'wages', label: 'Wages', icon: Users },
  { value: 'allowances', label: 'Allowances', icon: DollarSign },
  { value: 'other', label: 'Other', icon: Receipt }
];

const formatMoney = (amount, currency = 'USD') => `${currency} ${Number(amount || 0).toFixed(2)}`;

const ExpensesPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [expenses, setExpenses] = useState([]);
  const [trips, setTrips] = useState([]);
  const [buses, setBuses] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  
  const [newExpense, setNewExpense] = useState({
    trip_id: '',
    bus_id: '',
    category: '',
    description: '',
    amount: '',
    currency: 'USD',
    fx_rate: 18.5,
    receipt_photo: '',
    staff_user_id: '',
    staff_name: ''
  });

  const [floatDialogOpen, setFloatDialogOpen] = useState(false);
  const [selectedTripForFloat, setSelectedTripForFloat] = useState(null);
  const [floatAmount, setFloatAmount] = useState('');
  const [floatCurrency, setFloatCurrency] = useState('USD');
  const [savingFloat, setSavingFloat] = useState(false);

  const canApprove = user?.role === 'station_superuser' || user?.role === 'overseer';

  const handleOpenFloatDialog = (trip) => {
    setSelectedTripForFloat(trip);
    setFloatAmount(String(trip.expense_allowance || ''));
    setFloatCurrency(trip.expense_currency || 'USD');
    setFloatDialogOpen(true);
  };

  const handleSaveTripFloat = async (e) => {
    e.preventDefault();
    if (!selectedTripForFloat) return;
    setSavingFloat(true);
    try {
      await axios.put(
        `${API_URL}/trips/${selectedTripForFloat.id}/expense-allowance`,
        { expense_allowance: parseFloat(floatAmount) || 0, expense_currency: floatCurrency },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trip expense float updated successfully');
      setFloatDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update trip float');
    } finally {
      setSavingFloat(false);
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, token, navigate]);

  const fetchData = async () => {
    try {
      const [expensesRes, tripsRes, busesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/expenses`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API_URL}/trips`),
        axios.get(`${API_URL}/buses`),
        axios.get(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      setExpenses(expensesRes.data);
      setTrips(tripsRes.data);
      setBuses(busesRes.data);
      setStaffUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await axios.post(`${API_URL}/expenses`, {
        ...newExpense,
        amount: parseFloat(newExpense.amount),
        fx_rate: 1,
        trip_id: newExpense.trip_id || null,
        bus_id: newExpense.bus_id || null,
        staff_user_id: newExpense.staff_user_id || null,
        staff_name: newExpense.staff_name || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Expense logged successfully');
      setShowCreate(false);
      setNewExpense({
        trip_id: '',
        bus_id: '',
        category: '',
        description: '',
        amount: '',
        currency: 'USD',
        fx_rate: 18.5,
        receipt_photo: '',
        staff_user_id: '',
        staff_name: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log expense');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      await axios.post(`${API_URL}/expenses/${expenseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve expense');
    }
  };

  const handleReject = async (expenseId) => {
    const reason = prompt('Reason for rejection:');
    if (!reason) return;
    
    try {
      await axios.post(`${API_URL}/expenses/${expenseId}/reject`, null, {
        params: { reason },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Expense rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject expense');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getCategoryIcon = (category) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : Receipt;
  };

  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         e.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || e.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalByCurrency = (status, currency) => expenses
    .filter(e => e.status === status && e.currency === currency)
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-panashe-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Expense Tracking</h1>
            <p className="text-slate-500">Log and manage operational expenses</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="btn-panashe" data-testid="log-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                Log Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Log New Expense</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateExpense} className="space-y-4">
                {/* Category */}
                <div>
                  <Label>Category *</Label>
                  <Select 
                    value={newExpense.category} 
                    onValueChange={(v) => setNewExpense({...newExpense, category: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Staff Member Selector for Salaries, Wages, Allowances */}
                {['salaries', 'wages', 'allowances', 'driver_allowance'].includes(newExpense.category) && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                    <Label className="text-blue-900 font-semibold flex items-center gap-1.5 text-xs">
                      <Users className="h-4 w-4 text-blue-600" />
                      Select User / Staff Member of the System *
                    </Label>
                    <Select
                      value={newExpense.staff_user_id}
                      onValueChange={(val) => {
                        const sel = staffUsers.find(u => u.id === val);
                        setNewExpense(prev => ({
                          ...prev,
                          staff_user_id: val,
                          staff_name: sel ? sel.full_name : '',
                          description: prev.description || `${prev.category.toUpperCase()} payment to ${sel ? sel.full_name : ''}`
                        }));
                      }}
                    >
                      <SelectTrigger className="bg-white border-blue-300">
                        <SelectValue placeholder="Choose user of the system..." />
                      </SelectTrigger>
                      <SelectContent>
                        {staffUsers.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name} ({u.role?.replace('_', ' ')}) {u.phone ? `• ${u.phone}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newExpense.staff_name && (
                      <p className="text-xs text-blue-700 font-medium">
                        ✓ Payment assigned to: <span className="font-bold">{newExpense.staff_name}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Description */}
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                    placeholder="Describe the expense..."
                    required
                  />
                </div>

                {/* Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newExpense.amount}
                      onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select 
                      value={newExpense.currency} 
                      onValueChange={(v) => setNewExpense({...newExpense, currency: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="ZAR">ZAR</SelectItem>
                        <SelectItem value="ZWL">ZWL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Optional Links */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Trip (optional)</Label>
                    <Select 
                      value={newExpense.trip_id} 
                      onValueChange={(v) => setNewExpense({...newExpense, trip_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trip" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {trips.slice(0, 20).map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.trip_number || t.id.slice(0, 8)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bus (optional)</Label>
                    <Select 
                      value={newExpense.bus_id || 'none'} 
                      onValueChange={(v) => setNewExpense({...newExpense, bus_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select bus" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {buses.map(b => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.registration_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Trip Float Deduction Breakdown */}
                {(() => {
                  const sTrip = trips.find(t => t.id === newExpense.trip_id);
                  if (!sTrip) return null;
                  const allowance = Number(sTrip.expense_allowance || 0);
                  const spent = Number(sTrip.expenses_spent || 0);
                  const currentBal = sTrip.expense_balance !== undefined ? Number(sTrip.expense_balance) : allowance;
                  const currentAmt = parseFloat(newExpense.amount) || 0;
                  const tripCurrency = sTrip.expense_currency || 'USD';
                  const sameCurrency = newExpense.currency === tripCurrency;
                  const rem = sameCurrency ? currentBal - currentAmt : currentBal;

                  return (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-xs space-y-1.5">
                      <div className="flex justify-between items-center text-purple-900 font-semibold border-b border-purple-200 pb-1">
                        <span>🚌 Trip Float Deduction</span>
                        <span>{sTrip.trip_number || sTrip.id.slice(0, 8)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Initial Allocated Float:</span>
                        <span className="font-medium">{formatMoney(allowance, tripCurrency)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Already Deducted / Spent:</span>
                        <span className="font-medium text-red-600">-{formatMoney(spent, tripCurrency)}</span>
                      </div>
                      <div className="flex justify-between text-slate-800 font-semibold">
                        <span>Current Available Float:</span>
                        <span className={currentBal <= 0 ? 'text-red-600' : 'text-emerald-700'}>
                          {formatMoney(currentBal, tripCurrency)}
                        </span>
                      </div>
                      {currentAmt > 0 && (
                        <div className={`flex justify-between font-bold pt-1 border-t border-purple-200 ${rem < 0 ? 'text-red-600' : 'text-purple-900'}`}>
                          <span>Remaining Balance After Deduction:</span>
                          <span>{formatMoney(rem, tripCurrency)}</span>
                        </div>
                      )}
                      {!sameCurrency && currentAmt > 0 && (
                        <p className="text-amber-700 font-medium text-[11px] pt-0.5">
                          This {newExpense.currency} expense will not deduct from the {tripCurrency} float.
                        </p>
                      )}
                      {rem < 0 && (
                        <p className="text-red-600 font-medium text-[11px] pt-0.5">
                          This expense exceeds the allocated trip float by {formatMoney(Math.abs(rem), tripCurrency)}
                        </p>
                      )}
                    </div>
                  );
                })()}

                <Button type="submit" className="w-full btn-panashe" disabled={creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Log Expense
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Float Edit Dialog */}
        <Dialog open={floatDialogOpen} onOpenChange={setFloatDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Allocate / Update Trip Expense Float</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveTripFloat} className="space-y-4 mt-2">
              <p className="text-xs text-slate-600">
                Set the trip expense budget given to crew for trip <strong>{selectedTripForFloat?.trip_number}</strong> ({selectedTripForFloat?.origin} → {selectedTripForFloat?.destination}).
              </p>
              <div>
                <Label>Expense Float Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={floatAmount}
                  onChange={(e) => setFloatAmount(e.target.value)}
                  placeholder="e.g. 150.00"
                  required
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={floatCurrency} onValueChange={setFloatCurrency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="ZAR">ZAR (R)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full btn-panashe" disabled={savingFloat}>
                {savingFloat ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Trip Float
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Approval</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatMoney(totalByCurrency('pending', 'USD'), 'USD')}
                    <span className="block text-sm font-semibold text-yellow-700">{formatMoney(totalByCurrency('pending', 'ZAR'), 'ZAR')}</span>
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Receipt className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved (This Period)</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatMoney(totalByCurrency('approved', 'USD'), 'USD')}
                    <span className="block text-sm font-semibold text-green-700">{formatMoney(totalByCurrency('approved', 'ZAR'), 'ZAR')}</span>
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Expenses Logged</p>
                  <p className="text-2xl font-bold">{expenses.length}</p>
                </div>
                <div className="p-3 bg-slate-100 rounded-full">
                  <FileText className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trip Expense Floats & Deductions Overview */}
        <Card className="card-panashe border-purple-200">
          <CardHeader className="pb-3 border-b bg-purple-50/50">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-purple-900">
                <Fuel className="h-5 w-5 text-purple-600" />
                Trip Expense Floats &amp; Live Balances
              </CardTitle>
              <Badge className="bg-purple-100 text-purple-700">Deducts on spend</Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {trips.slice(0, 6).map(trip => {
                const allowance = Number(trip.expense_allowance || 0);
                const spent = Number(trip.expenses_spent || 0);
                const bal = trip.expense_balance !== undefined ? Number(trip.expense_balance) : allowance;
                const pct = allowance > 0 ? Math.min(100, Math.round((spent / allowance) * 100)) : 0;
                const departureDate = trip.departure_datetime ? parseISO(trip.departure_datetime) : null;
                const departureLabel = departureDate && !Number.isNaN(departureDate.getTime())
                  ? format(departureDate, 'dd MMM yyyy, HH:mm')
                  : 'Date not set';

                return (
                  <div key={trip.id} className="p-3 border rounded-lg bg-white shadow-sm space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-sm text-slate-900">{trip.trip_number || trip.id.slice(0, 8)}</p>
                        <p className="flex items-center gap-1 text-xs text-slate-600">
                          <Calendar className="h-3 w-3" />
                          {departureLabel}
                        </p>
                        <p className="text-xs text-slate-500">{trip.origin || 'Harare'} → {trip.destination || 'Durban'}</p>
                        <p className="text-[11px] text-slate-500">Bus: {trip.bus_registration || 'ADF 2026'}</p>
                      </div>
                      {canApprove && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-xs text-purple-700 hover:bg-purple-50 px-2"
                          onClick={() => handleOpenFloatDialog(trip)}
                        >
                          {allowance > 0 ? 'Edit Float' : '+ Set Float'}
                        </Button>
                      )}
                    </div>

                    <div className="space-y-1 text-xs pt-1 border-t border-slate-100">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Allocated Float:</span>
                        <span className="font-semibold">{formatMoney(allowance, trip.expense_currency || 'USD')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Spent / Deducted:</span>
                        <span className="font-semibold text-red-600">-{formatMoney(spent, trip.expense_currency || 'USD')}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-1">
                        <span className="text-slate-700">Remaining Balance:</span>
                        <span className={bal <= 0 && allowance > 0 ? 'text-red-600' : 'text-emerald-700'}>
                          {formatMoney(bal, trip.expense_currency || 'USD')}
                        </span>
                      </div>
                    </div>

                    {allowance > 0 && (
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden mt-1">
                        <div 
                          className={`h-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-yellow-500' : 'bg-emerald-500'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search expenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Expenses List */}
        <div className="space-y-3">
          {filteredExpenses.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                <Receipt className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No expenses found</p>
              </CardContent>
            </Card>
          ) : (
            filteredExpenses.map((expense) => {
              const CategoryIcon = getCategoryIcon(expense.category);
              return (
                <Card key={expense.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <CategoryIcon className="h-6 w-6 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColor(expense.status)}>
                              {expense.status.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {expense.category.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="font-medium">{expense.description}</p>
                          {expense.staff_name && (
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-blue-800 font-semibold bg-blue-50 px-2 py-0.5 rounded border border-blue-200 w-fit">
                              <UserCheck className="h-3.5 w-3.5 text-blue-600" />
                              <span>Paid to Staff: <strong>{expense.staff_name}</strong></span>
                            </div>
                          )}
                          <p className="text-sm text-slate-500 mt-1">
                            by {expense.created_by_name} • {expense.created_at && format(parseISO(expense.created_at), 'dd MMM yyyy HH:mm')}
                          </p>
                          {expense.trip_id && (
                            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 w-fit">
                              <span>🚌 Trip Expense</span>
                              {expense.trip_expense_balance !== null && expense.trip_expense_balance !== undefined && (
                                <span>• Float Remaining: <strong>{formatMoney(expense.trip_expense_balance, expense.trip_expense_currency || 'USD')}</strong></span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-heading text-xl font-bold">
                          {formatMoney(expense.amount, expense.currency)}
                        </p>
                        
                        {canApprove && expense.status === 'pending' && (
                          <div className="flex gap-2 mt-2">
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handleApprove(expense.id)}
                              data-testid={`approve-${expense.id}`}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleReject(expense.id)}
                              data-testid={`reject-${expense.id}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExpensesPage;
